import { Typography } from "antd";
import {
  CreditorPayFormat,
  InitialPaymentPercentageOf,
  InterestCalculationStrategy,
  PaymentType,
  ProgramSplitFormat,
  StrategyConfigV2,
  StrategyRuleKeys,
  TargetWeeklyCalculationStrategy,
} from "dtos/strategy.dto";
import { formatMoney, toTitleCase } from "utils/functions";
import { derivePurpose, StrategyPurpose } from "./functions";

const PURPOSE_LABELS: Record<StrategyPurpose, string> = {
  settlement: "Settlement",
  negotiate: "Negotiate",
  lawsuit: "Lawsuit",
};

type Row = [label: string, value: string];

const money = (value?: number) => formatMoney(value ?? 0) ?? "$0";
const pct = (value?: number) => `${Number(((value ?? 0) * 100).toFixed(2))}%`;
const title = (value?: string) =>
  value ? (toTitleCase(value.replace("-", "_"), { sep: "_", forceLower: true }) ?? value) : "—";

const paymentLabel = (s: StrategyConfigV2) => {
  if (s.payment_type !== PaymentType.MONTHLY) return title(s.payment_type);
  if (s.target_weekly_calculation_strategy === TargetWeeklyCalculationStrategy.COMPACT) return "Monthly · compact";
  if (s.target_weekly_calculation_strategy === TargetWeeklyCalculationStrategy.PRIOR_BIAS)
    return "Monthly · 4 × weekly";
  return `Monthly · ${title(s.target_weekly_calculation_strategy)}`;
};

const commissionLabel = (s: StrategyConfigV2) => {
  if (s.interest_calculation_strategy === InterestCalculationStrategy.PERCENTAGE || !s.interest_calculation_strategy)
    return `${pct(s.program_fee)} program + ${pct(s.success_fee)} success`;
  if (s.interest_calculation_strategy === InterestCalculationStrategy.FIXED_PER_LAWSUIT)
    return `${money(s.interest_amount)} per lawsuit`;
  return `${money(s.interest_amount)} · ${title(s.interest_calculation_strategy)}`;
};

const splitLabel = (s: StrategyConfigV2) => {
  const meta = s.program_split?.meta ?? {};
  switch (s.program_split?.format) {
    case ProgramSplitFormat.UNIFIED:
      return "Across the whole plan";
    case ProgramSplitFormat.EARLY_STOPPING_WEEKS:
      return meta.mode === "fixed"
        ? `For ${meta.fixed_weeks ?? "—"} weeks (all debt sizes)`
        : `Weeks by ${meta.base === "interest" ? "total interest" : "total debt"} range`;
    case ProgramSplitFormat.EARLY_STOPPING_PERCENTAGE:
      return `Until ${meta.percentage ?? 0}% of the plan`;
    case ProgramSplitFormat.PERCENTAGE_OF_WEEKLY:
      return `${meta.percentage ?? 0}% of each payment`;
    default:
      return title(s.program_split?.format);
  }
};

const pushbackLabel = (value = 0) => {
  if (!value) return "None";
  return `${value > 0 ? "▲ Increase" : "▼ Decrease"} ${pct(Math.abs(value))}`;
};

const RULE_LABELS: Record<StrategyRuleKeys, string> = {
  [StrategyRuleKeys.MAX_TENURE]: "Max tenure",
  [StrategyRuleKeys.MIN_TENURE]: "Min tenure",
  [StrategyRuleKeys.MAX_PROGRAM_TENURE]: "Max commission tenure",
  [StrategyRuleKeys.MIN_PROGRAM_TENURE]: "Min commission tenure",
  [StrategyRuleKeys.MAX_WEEKLY_PAYMENT]: "Max weekly payment",
  [StrategyRuleKeys.MIN_WEEKLY_PAYMENT]: "Min weekly payment",
  [StrategyRuleKeys.MAX_INTEREST]: "Max commission",
  [StrategyRuleKeys.MIN_INTEREST]: "Min commission",
};

export const getSummaryGroups = (s: StrategyConfigV2, templateName?: string): { title: string; rows: Row[] }[] => {
  const timeline = s.program_split?.initial_payment_timeline ?? 0;
  const groups: { title: string; rows: Row[] }[] = [
    {
      title: "Purpose",
      rows: [
        ["Goal", PURPOSE_LABELS[derivePurpose(s)]],
        ["Format", "Manager approval"],
      ],
    },
    {
      title: "Payments & fees",
      rows: [
        ["Payment", paymentLabel(s)],
        [
          "Initial fee",
          `${pct(s.initial_fee)} of ${s.initial_fee_base === InitialPaymentPercentageOf.TOTAL_INTEREST ? "interest" : "debt"}`,
        ],
        ["Upfront amount", `${money(s.initial_bias_amount)} · ${s.is_bias_exclusive ? "exclusive" : "inclusive"}`],
        ["Commission", commissionLabel(s)],
        ["Weekly adjustment", pushbackLabel(s.dynamic_weekly_pushback)],
      ],
    },
    {
      title: "Commission",
      rows: [
        ["Collected as", splitLabel(s)],
        ["Initial payment", timeline ? `Split over ${timeline + 1} payments` : "With the first payment"],
      ],
    },
    {
      title: "Creditors & details",
      rows: [
        ["Payback ratio", pct(s.creditor_split?.total_payback)],
        ["Pay via", s.creditor_split?.pay_format === CreditorPayFormat.DIRECT ? "Direct" : "FCDS"],
        ["PandaDoc template", templateName ?? (s.template_id ? "Selected" : "None")],
      ],
    },
  ];

  const advanced: Row[] = [];
  if (s.fallback_display_timeline != null)
    advanced.push(["Display timeline multiplier", pct(s.fallback_display_timeline)]);
  if (s.service_fee?.length) advanced.push(["Service fee", `${s.service_fee.length} custom rule(s)`]);
  if (s.legal_fee?.length) advanced.push(["Legal fee", `${s.legal_fee.length} custom rule(s)`]);
  s.rules?.forEach((rule) => advanced.push([RULE_LABELS[rule.key] ?? rule.key, String(rule.value)]));
  if (advanced.length) groups.push({ title: "Advanced", rows: advanced });

  return groups;
};

const StrategySummary = ({ strategy, templateName }: { strategy: StrategyConfigV2; templateName?: string }) => (
  <div className="flex flex-col gap-3">
    {getSummaryGroups(strategy, templateName).map((group, i) => (
      <div key={group.title} className="border border-solid border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-3.5 py-2.5 bg-gray-50 border-0 border-b border-solid border-gray-100">
          <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-800 text-[11px] font-bold flex items-center justify-center">
            {i < 4 ? i + 1 : "+"}
          </span>
          <Typography.Text className="!text-[13px] font-semibold">{group.title}</Typography.Text>
        </div>
        <div className="px-3.5 py-1">
          {group.rows.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3 py-1.5 text-[13px]">
              <span className="text-gray-600">{label}</span>
              <span className="font-medium text-right">{value}</span>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default StrategySummary;
